import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import RowDelete from '../ui/RowDelete';

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface UserWithOrganization {
    id: string;
    email: string;
    name: string;
    role: string;
    created_at: string | null;
    organization_id: string;
    organization_name: string;
}

interface PaginatedUsersResponse {
    users: UserWithOrganization[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
}

interface UsersTabProps {
    onEditUser: (user: UserWithOrganization) => void;
    refreshTrigger?: number;
}

const UsersTab: React.FC<UsersTabProps> = ({ onEditUser, refreshTrigger }) => {
    const [usersData, setUsersData] = useState<PaginatedUsersResponse>({
        users: [],
        total: 0,
        page: 1,
        per_page: 50,
        total_pages: 0
    });
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [currentUsersPage, setCurrentUsersPage] = useState(1);
    const [usersPerPage, setUsersPerPage] = useState(50);
    const { get, remove } = useApi();

    const getAllUsers = async (page: number = 1, search: string = '', perPage: number = 50) => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: perPage.toString(),
            });
            if (search) {
                params.append('search', search);
            }
            
            const response = await get(`/admin/users/?${params.toString()}`);
            setUsersData(response.data || {
                users: [],
                total: 0,
                page: 1,
                per_page: 50,
                total_pages: 0
            });
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
            // Set safe default data on error
            setUsersData({
                users: [],
                total: 0,
                page: 1,
                per_page: 50,
                total_pages: 0
            });
        }
    };

    const handleDeleteUser = async (userId: string) => {
        try {
            await remove(`/admin/users/${userId}`);
            toast.success('User deleted successfully');
            // Refresh the users list in the main table
            getAllUsers(currentUsersPage, userSearchTerm, usersPerPage);
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user');
        }
    };

    const handleUsersSearch = (searchTerm: string) => {
        setUserSearchTerm(searchTerm);
        setCurrentUsersPage(1);
        getAllUsers(1, searchTerm, usersPerPage);
    };

    const handleUsersPageChange = (newPage: number) => {
        setCurrentUsersPage(newPage);
        getAllUsers(newPage, userSearchTerm, usersPerPage);
    };

    const handleUsersPerPageChange = (newPerPage: string) => {
        const perPage = parseInt(newPerPage);
        setUsersPerPage(perPage);
        setCurrentUsersPage(1);
        getAllUsers(1, userSearchTerm, perPage);
    };

    useEffect(() => {
        getAllUsers();
    }, []);

    // Refresh users when trigger changes
    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            getAllUsers(currentUsersPage, userSearchTerm, usersPerPage);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger]);

    return (
        <TabsContent value="users" className="space-y-4">
            <div className="flex gap-4 items-center">
                <Input
                    type="text"
                    placeholder="Search users by name, email, or organization..."
                    value={userSearchTerm}
                    onChange={(e) => handleUsersSearch(e.target.value)}
                    className="flex-1"
                />
                <Select value={usersPerPage.toString()} onValueChange={handleUsersPerPageChange}>
                    <SelectTrigger className="w-[100px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="max-h-[calc(100vh-250px)] overflow-y-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Organization</TableHead>
                            <TableHead>Created At</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {usersData?.users?.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>{user.name}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>{user.role}</TableCell>
                                <TableCell>{user.organization_name}</TableCell>
                                <TableCell>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end space-x-2">
                                        <Button 
                                            variant="ghost"
                                            onClick={() => onEditUser(user)}
                                        >
                                            Edit
                                        </Button>
                                        <RowDelete
                                            deleteCallback={() => handleDeleteUser(user.id)}
                                            deleteMessage={`Are you sure you want to delete user ${user.name}?`}
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        )) || []}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                    Showing {Math.min((currentUsersPage - 1) * usersPerPage + 1, usersData?.total || 0)} to {Math.min(currentUsersPage * usersPerPage, usersData?.total || 0)} of {usersData?.total || 0} users
                </div>
                <div className="flex items-center space-x-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUsersPageChange(currentUsersPage - 1)}
                        disabled={currentUsersPage <= 1}
                    >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                    </Button>
                    <span className="text-sm">
                        Page {currentUsersPage} of {usersData?.total_pages || 0}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUsersPageChange(currentUsersPage + 1)}
                        disabled={currentUsersPage >= (usersData?.total_pages || 0)}
                    >
                        Next
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </TabsContent>
    );
};

export default UsersTab;